import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
} from "react";
import { Spinner } from "react-bootstrap";
import { ChatDots } from "react-bootstrap-icons";
import { ChatMessagesProps } from "./chat-messages.props";
import { ChatMessagesInput } from "./input";
import { useAuthStore } from "@/features/auth";
import { Message, useMessagesStore } from "@/entities/message";
import { ChatMessagesHeader } from "./header";
import { useChatStore } from "@/entities/chat";
import { NewChatModal } from "./new-chat-modal";
import { useNavigate } from "react-router-dom";
import { MessageActionBar } from "./message-action-bar";
import {
  useModalWithVoiceGuard,
  useMessageSelectionStore,
  useScrollStore,
} from "../../model";
import { ScrollDownButton } from "../../lib";
import { ChatMessageList } from "./chat-message-list/chat-message-list";
import "./chat-messages.css";
import { User } from "@/entities/user";
import { ConfirmVoiceModal } from "@/shared/ui/confirm-voice-modal";
import { useMediaQuery } from "react-responsive";
import { SelectChatModal } from "./search-chat-modal";

const ChatMessagesComponent = ({
  name,
  messages,
  chat,
  registeredContact,
  chatsLength,
}: ChatMessagesProps) => {
  const { user, isAdmin } = useAuthStore();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const loadingChatMessages = useMessagesStore(
    (state) => state.loadingChatMessages
  );
  const loadingMessage = useMessagesStore((state) => state.loadingMessage);
  const deleteMessage = useMessagesStore((state) => state.deleteMessage);
  const updateMessage = useMessagesStore((state) => state.updateMessage);
  const replyingToMessage = useMessagesStore(
    (state) => state.replyingToMessage
  );
  const inputTitle = useMessagesStore((state) => state.inputTitle);
  const setInputTitle = useMessagesStore((state) => state.setInputTitle);
  const editingToMessage = useMessagesStore((state) => state.editingMessage);
  const sendMessage = useMessagesStore((state) => state.sendMessage);
  const resendMessage = useMessagesStore((state) => state.resendMessage);

  const setForwardMessage = useMessagesStore((state) => state.setForwardMessage);
  const activeMsg = useMessagesStore((state) => state.activeMsg[chat?._id ?? ""]);
  const selectedMessages = useMessageSelectionStore((state) => state.selectedMessages);

  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null);
  const isTouchDevice = useMediaQuery({ maxWidth: 1024 });
  const [showSelectChatModal, setShowSelectChatModal] = useState(false);

  const replyingMessage = useMemo(
    () => (chat ? replyingToMessage[chat._id] : null),
    [chat, replyingToMessage]
  );
  const editingMessage = useMemo(
    () => (chat ? editingToMessage[chat._id] : null),
    [chat, editingToMessage]
  );

  const isLoading = useMemo(() => {
    return chat?._id ? !!loadingChatMessages?.[chat._id] : false;
  }, [chat?._id, loadingChatMessages]);

  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<"recording" | "voiceBlob" | null>(
    null
  );

  const { checkAndOpen, runDeferred } = useModalWithVoiceGuard();

  const handleOpenNewChat = useCallback(() => {
    const action = () => setShowNewChatModal(true);
    checkAndOpen(action, setModalType, setShowModal);
  }, [checkAndOpen]);

  const modeSelected = useMessageSelectionStore((state) => state.modeSelected);

  const deselectMessages = useMessageSelectionStore(
    (state) => state.deselectMessages
  );
  const resetSelection = useMessageSelectionStore(
    (state) => state.resetSelection
  );

  const deleteMessagesHandler = useCallback(
    async (ids: string[]) => {
      if (!chat || ids.length === 0) return;
      deselectMessages(ids);
      resetSelection();
      deleteMessage(chat._id, ids);
    },
    [chat]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && modeSelected) {
        resetSelection();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modeSelected, resetSelection]);

  const setEditingMessageHandler = useCallback(
    (chatId: string, message: Message | null) => {
      useMessagesStore.getState().setEditingMessage(chatId, message);
    },
    []
  );

  const setReplyingToMessageHandler = useCallback(
    (chatId: string, message: Message | null) => {
      useMessagesStore.getState().setReplyingToMessage(chatId, message);
    },
    []
  );

  const handleReply = useCallback(
    (msg: Message) => {
      if (!loadingMessage[msg._id] && chat) {
        setReplyingToMessageHandler(chat._id, msg);
      }
    },
    [loadingMessage, chat, setReplyingToMessageHandler]
  );

  const handleDoubleClick = useCallback(
    (msg: Message) => {
      if (modeSelected || loadingMessage[msg._id]) return;

      if (!isTouchDevice) {
        chatInputRef.current?.focus();
      }

      if (chat) setReplyingToMessageHandler(chat._id, msg);
    },
    [modeSelected, loadingMessage, chat, setReplyingToMessageHandler, isTouchDevice]
  );

  const onSendHandler = useCallback(
    (
      user: User,
      chatId: string,
      msg: string,
      contactUserId: string,
      replyingMessage: Message | null,
      editingMessage: Message | null,
      files: File[]
    ) => {
      setReplyingToMessageHandler(chatId, null);
      setEditingMessageHandler(chatId, null);

      sendMessage(
        user,
        chatId,
        contactUserId,
        msg,
        replyingMessage ?? undefined,
        editingMessage ?? undefined,
        files
      );
    },
    [setEditingMessageHandler, setReplyingToMessageHandler]
  );

  const triggerScrollToMessage = useCallback(
    (chatId: string, messageId: string) => {
      const scrollToFn = useScrollStore.getState().getScrollToMessage(chatId);
      scrollToFn?.(chatId, messageId);
    },
    []
  );

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (!isLoading) {
      timer = setTimeout(() => {
        setVisible(true);
      }, 100);
    } else {
      setVisible(false);
    }

    return () => clearTimeout(timer);
  }, [isLoading]);

  const [attachedFiles, setAttachedFiles] = useState<Record<string, File[]>>(
    {}
  );
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounter = useRef(0);

  const canDrop = messages && !!chat?._id;

  const handleDragEnter = (e: React.DragEvent) => {
    if (!canDrop) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.types.includes("Files")) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!canDrop) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDraggingOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!canDrop) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!canDrop) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDraggingOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setAttachedFiles((prev) => {
        const current = prev[chat._id] || [];
        return {
          ...prev,
          [chat._id]: [...current, ...Array.from(files)],
        };
      });
    }
  };

  useEffect(() => {
    if (isTouchDevice) return;

    setTimeout(() => {
      chatInputRef.current?.focus();
    }, 200);
  }, [attachedFiles, showNewChatModal, showModal, name, messages]);

  useEffect(() => {
    if (isTouchDevice) return;

    const handleClick = () => {
      setTimeout(() => {
        chatInputRef.current?.focus();
      }, 200);
    };

    const container = messagesContainerRef.current;
    container?.addEventListener("click", handleClick);

    return () => {
      container?.removeEventListener("click", handleClick);
    };
  }, [isTouchDevice]);

  const chatLayoutRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    const handleResize = () => {
      if (!isTouchDevice) return;

      if (!window.visualViewport || !chatLayoutRef.current) return;
      const offset = window.innerHeight - window.visualViewport.height;
      const safeOffset = Math.max(16, offset);
      chatLayoutRef.current.style.setProperty('padding-bottom', `${safeOffset}px`, 'important');
    };

    handleResize();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport?.removeEventListener('resize', handleResize);
    }
  }, []);

  return (
    <div
      ref={chatLayoutRef}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      className="d-flex flex-column h-100 border rounded-5 p-3 position-relative"
    >
      {canDrop && isDraggingOver && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            border: "2px dashed #007bff",
            borderRadius: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
            pointerEvents: "none",
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 600, color: "#007bff" }}>
            Вставить
          </div>
        </div>
      )}

      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 71,
            left: 0,
            width: "100%",
            height: "calc(100% - 71px)",
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 205,
          }}
        >
          <Spinner
            animation="border"
            variant="secondary"
            style={{ width: 50, height: 50 }}
          />
        </div>
      )}

      <ChatMessagesHeader
        name={name}
        userId={user?._id}
        chatId={chat?._id}
        contact={chat?.contact}
        isAdmin={isAdmin}
        onClick={handleOpenNewChat}
        onDeleteMessages={deleteMessagesHandler}
        onUpdateContact={(chatId, firstName, lastName) => {
          useChatStore
            .getState()
            .updateChatContact(chatId, firstName, lastName);
        }}
        onForwardMessages={() => {
          // if (!chatId) return;
          // console.log(selectedMessages);
          
          setShowSelectChatModal(true);
          // const { modeSelected, selectedMessages, resetSelection } =
          // useMessageSelectionStore();
          //  setForwardMessage(chatId, selectedMessages, chatId);

        }}
      />

      <hr className="mt-2 mb-3 w-100" />

      <div
        ref={messagesContainerRef}
        className={`scrollbar-custom-y overlow-hidden rounded-bottom-4 flex-grow-1 mb-${isMobile ? 0 : 3} position-relative`}
        style={{
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          scrollBehavior: "smooth",
        }}
      >
        {!messages && visible && !isLoading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center text-secondary"
            style={{
              gap: 10,
              opacity: 0.8,
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            <ChatDots size={50} />
            {(isAdmin || chatsLength > 0) && (
              <div className="fs-5 fw-medium" style={{ textAlign: "center" }}>
                {!chat?._id
                  ? "Выберите пользователя, с кем хотите начать диалог"
                  : !registeredContact
                    ? "Ждём пользователя"
                    : "Начните диалог"}
              </div>
            )}
          </div>
        )}

        {chat && (
          <ChatMessageList
            chatContainerRef={chatContainerRef}
            chatId={chat._id}
            currentChat={chat}
            registeredContact={registeredContact}
            onReply={handleReply}
            onDoubleClick={handleDoubleClick}
            onResend={(msg) => {
              if (chat && user) {
                resendMessage(user, msg, chat._id, chat.contact.user._id);
              }
            }}
            onDeleteMessage={(ids) => deleteMessage(chat._id, ids)}
            onEditMessage={(text) => console.log("Edit:", text)}
            setShowSelectChatModal={setShowSelectChatModal}
            forwardSelectedMessages={() => setShowSelectChatModal(true)}
          />
        )}
      </div>

      {editingMessage && chat?._id === editingMessage.chat._id ? (
        <MessageActionBar
          mode="edit"
          message={editingMessage}
          currentChatId={chat._id}
          user={user}
          scrollToMessage={(id) => triggerScrollToMessage(chat._id, id)}
          onCancel={(chatId) => setEditingMessageHandler(chatId, null)}
          onDeleteFile={(file) => {
            if (!editingMessage) return;

            const newUrls =
              editingMessage.fileUrls?.filter((url) => url !== file) || [];

            const updatedMessage = {
              ...editingMessage,
              fileUrls: newUrls,
            };

            useMessagesStore
              .getState()
              .setEditingMessage(chat._id, updatedMessage);
          }}
        />
      ) : replyingMessage && chat?._id === replyingMessage.chat._id ? (
        <MessageActionBar
          mode="reply"
          message={replyingMessage}
          currentChatId={chat._id}
          user={user}
          scrollToMessage={(id) => triggerScrollToMessage(chat._id, id)}
          onCancel={(chatId) => setReplyingToMessageHandler(chatId, null)}
        />
      ) : null}

      {chat && (
        <div className="position-relative">
          <ScrollDownButton
            chatId={chat?._id}
            containerRef={chatContainerRef}
            replyingMessage={!!replyingMessage}
            editingMessage={!!editingMessage}
          />

          <ChatMessagesInput
            value={inputTitle[chat._id]?.text || ""}
            setValue={(value) => {
              user && setInputTitle(chat._id, value);
            }}
            textareaRef={chatInputRef}
            disabled={false}
            // disabled={!registeredContact}
            files={chat?._id ? attachedFiles[chat._id] || [] : []}
            setFiles={(updater) => {
              if (!chat?._id) return;

              setAttachedFiles((prev) => {
                const current = prev[chat._id] || [];
                const newFiles =
                  typeof updater === "function" ? updater(current) : updater;
                return {
                  ...prev,
                  [chat._id]: newFiles,
                };
              });
            }}
            onSend={(msg, files) => {
              if (chat && user) {
                if (editingMessage) {
                  updateMessage(
                    chat._id,
                    editingMessage._id,
                    msg,
                    files,
                    editingMessage.fileUrls || []
                  );
                  setEditingMessageHandler(chat._id, null);
                } else {
                  onSendHandler(
                    user,
                    chat._id,
                    msg,
                    chat.contact.user._id,
                    replyingMessage,
                    editingMessage,
                    files
                  );
                }
              }

              if (chat._id) {
                setAttachedFiles((prev) => {
                  const updated = { ...prev };
                  delete updated[chat._id];
                  return updated;
                });
              }
            }}
          />
        </div>
      )}

      <NewChatModal
        show={showNewChatModal}
        addNewChat={(newContact) => {
          useChatStore.getState().addNewChat(newContact)
            .catch(e => console.error(e)
            )
        }}
        onHide={() => setShowNewChatModal(false)}
        onChatSelected={(chat) => {
          useChatStore.getState().selectChat(chat);
          navigate(`/?chat=${chat._id}`);
        }}
      />

      <SelectChatModal
        show={showSelectChatModal}
        onHide={() => setShowSelectChatModal(false)}
        onSelect={(chatIds) => {
          if (activeMsg) {
            setForwardMessage(activeMsg.msg.chat._id, [activeMsg.msg._id], chatIds, navigate);
            resetSelection()
          } else {
            if (chat) {
              setForwardMessage(chat._id, Array.from(selectedMessages), chatIds, navigate)
              resetSelection()
            }
          }
        }}
      />

      <ConfirmVoiceModal
        show={showModal}
        type={modalType}
        onClose={() => {
          setShowModal(false);
          setModalType(null);
        }}
        onConfirm={() => {
          runDeferred();
        }}
      />
    </div>
  );
};

export const ChatMessages = React.memo(ChatMessagesComponent);
